'use client';

// useMediaQuery — reactive `matchMedia` boolean for a CSS media query, safe on
// the server and in jsdom.
//
// SHARED (sharing.md § 4 / § 1, ADR 030 Decision 2). Consolidates three
// hand-rolled `matchMedia` readers that had each grown their own copy of this
// wiring: `Sidebar`'s narrow-rail gate, the clerk console's increment-card fold,
// and its bidder-rail drawer-tier focus-trap gate. It is pure viewport
// *behavior* with zero appearance, so it belongs in the shared foundation beside
// `useViewerTimezone` rather than in either app. BL-1511 then moved bidder-web's
// own family of matchMedia gates (`useIsDesktopViewport`, the quick-bid /
// account-sheet shell switch, the three reduced-motion reads) onto it, so it is
// now the ONLY `matchMedia` call site in either app's client code.
//
// The callers keep their own named query constants and thin wrappers on purpose:
// a breakpoint band carries domain meaning ("the tier where the rail is a
// drawer") that a bare query string does not.

import { useCallback, useSyncExternalStore } from 'react';

/** Server / pre-hydration snapshot: no viewport, so nothing matches. */
const getServerSnapshot = (): boolean => false;

/**
 * The `MediaQueryList` for `query`, or `null` where `matchMedia` is unusable.
 *
 * Guards the two environments that lack it: the server (no `window`) and jsdom
 * (a `window` with no `matchMedia` implementation). Both fall back to the
 * `false` server snapshot, so a consumer renders correctly with no stub; a test
 * that wants to assert a MATCH still installs one, exactly as before.
 *
 * @param query - A CSS media query string.
 * @returns The `MediaQueryList`, or `null` when `matchMedia` is unavailable.
 */
function getMediaQueryList(query: string): MediaQueryList | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    return window.matchMedia(query);
}

/**
 * Reactively tracks whether `query` matches the current viewport, re-rendering
 * the consumer as the viewport crosses the boundary (resize / device rotation).
 *
 * Hydration-safe via `useSyncExternalStore`: SSR and the first client paint read
 * `false` (the server snapshot), then React re-reads the real match on the
 * client. Consumers that must not flash the matched state on first paint (an
 * overlay that traps focus, a sheet that opens) rely on that ordering — the same
 * `() => false` server snapshot the three hand-rolled readers each used.
 *
 * @param query - A CSS media query string, e.g. `'(max-width: 1023px)'`.
 * @returns `true` when the query currently matches, else `false`.
 */
export function useMediaQuery(query: string): boolean {
    const subscribe = useCallback(
        (onChange: () => void) => {
            const mq = getMediaQueryList(query);
            if (!mq) return () => {};
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        },
        [query],
    );
    const getSnapshot = useCallback(() => getMediaQueryList(query)?.matches ?? false, [query]);
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
